import type { ReactElement } from "react";

import classNames from "classnames";
import { Col, Container, Row } from "react-bootstrap";

import ChooseUseCaseModal from "@/components/chooseUseCaseModal";
import ScrollToTopButton from "@/components/scrollToTopButton";
import FolderDownloadTopBanner from "@/components/ui/folderDownloadTopBanner";
import GlobalWidgets from "@/components/widgets/globalWidgets";
import { ROUTES } from "@/constants/routes";
import { isWebView } from "@/constants/webView";
import useIsRouteIncluded from "@/hooks/useIsRouteIncluded";
import useViewMode from "@/hooks/useViewMode";
import webViewEventService from "@/services/webViewEvents/webViewEventService";
import { withSuspense } from "@/utils/hoc/withSuspense";

import styles from "./MainLayout.module.scss";

interface IMainLayoutProps {
  sidebar: ReactElement;
  content: ReactElement;
}

const NO_CONTAINER_ROUTES = [ROUTES.STATISTICS, ROUTES.STATISTICS_ID];
const WEB_VIEW_NO_STORAGE_BANNER_ROUTES = [ROUTES.ENTRY];

const WebViewNoStorageBannerLazy = withSuspense(
  () => import("@/components/ui/webViewNoStorageBanner")
);

function MainLayout({ sidebar, content }: IMainLayoutProps) {
  const isNoContainer = useIsRouteIncluded(NO_CONTAINER_ROUTES);
  const isWebViewNoStorageBannerRoute = useIsRouteIncluded(
    WEB_VIEW_NO_STORAGE_BANNER_ROUTES
  );
  const { isLg } = useViewMode();

  const showWebViewNoStorageBanner =
    isWebView &&
    isWebViewNoStorageBannerRoute &&
    webViewEventService.isManageStorageBannerSupported();

  return (
    <>
      <GlobalWidgets />

      <div className={styles.shell}>
        {isLg && <FolderDownloadTopBanner />}

        <Container fluid className={styles.container}>
          <Row className={styles.wrapper}>
            <Col xs={12} lg className={styles.sidebar}>
              {sidebar}
            </Col>

            {!isLg && (
              <Col xs={12} className="p-0">
                <FolderDownloadTopBanner />
              </Col>
            )}

            {showWebViewNoStorageBanner && (
              <Col xs={12} className="p-0">
                <WebViewNoStorageBannerLazy />
              </Col>
            )}

            <Col
              as="main"
              xs={12}
              lg
              className={classNames("scrollbar", styles.content, {
                "p-0 pt-lg-4 pt-xxl-0 d-flex": isNoContainer,
              })}
            >
              {content}
            </Col>
          </Row>
        </Container>
      </div>

      <ScrollToTopButton />

      <ChooseUseCaseModal />
    </>
  );
}

export default MainLayout;
